import { useMemo } from "react";
import { useLocation } from "wouter";

export type DevTabsSource = "query" | "localStorage" | "env" | "disabled";

/**
 * Hook para detectar se preview de abas deve estar ativo
 * 
 * Flag ativa quando (em ordem de prioridade):
 * 1. Query param devTabs=1 em /clients
 * 2. localStorage.devTabs === "1" (fallback para preview sem query param)
 * 3. Env var VITE_ENABLE_CLIENT_TABS_PREVIEW=true
 * 
 * Por padrão retorna false (zero alterações visuais)
 * 
 * Usa wouter + fallback para window.location para máxima compatibilidade
 */
export function useClientsTabsPreview(): boolean {
  const location = useLocation();

  return useMemo(() => {
    const source = getDevTabsSource();
    
    if (source !== "disabled") {
      console.info("[DEV_TABS]", {
        enabled: true,
        source,
        path: typeof window !== "undefined" ? window.location.pathname : "unknown",
        search: typeof window !== "undefined" ? window.location.search : "unknown",
        timestamp: new Date().toISOString(),
      });
      return true;
    }

    return false;
  }, [location]);
}

/**
 * Detecta qual fonte ativou o DEV_TABS
 * Retorna a fonte ou "disabled" se não estiver ativo
 */
export function getDevTabsSource(): DevTabsSource {
  // Prioridade 1: Query param devTabs=1
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    if (params.get("devTabs") === "1") {
      return "query";
    }
  }

  // Prioridade 2: localStorage.devTabs === "1"
  if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
    try {
      if (localStorage.getItem("devTabs") === "1") {
        return "localStorage";
      }
    } catch (e) {
      // localStorage pode estar desabilitado
    }
  }

  // Prioridade 3: Env var
  if (import.meta.env.VITE_ENABLE_CLIENT_TABS_PREVIEW === "true") {
    return "env";
  }

  return "disabled";
}
